import { useEffect, useState } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import { Card, CardContent } from "../components/ui/CCard";

export default function ReviewSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [therapyType, setTherapyType] = useState('ALL');

    useEffect(() => {
        fetchSettings();
    }, [therapyType]);

    const fetchSettings = async () => {
        try {
            const res = await api.getManualReviewSettings(therapyType === 'ALL' ? null : therapyType);
            const data = res.data;
            setSettings(data);
            // Update therapyType from response in case it was normalized
            if (data.therapy_type) {
                setTherapyType(data.therapy_type);
            }
        } catch (err) {
            setMessage({ type: 'error', text: `Failed to load settings: ${err.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        try {
            const res = await api.updateManualReviewSettings({ ...settings, therapy_type: therapyType });
            setSettings(res.data.settings);
            setMessage({ type: 'success', text: 'Settings saved successfully!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to save settings' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (window.confirm('Reset all settings to defaults?')) {
            setSettings({
                ...settings,
                confidence_threshold: 0.4,
                min_quality_score: 1.5,
                critical_failure_count: 2,
                error_threshold: 10,
                min_session_turns: 5,
                incoherence_threshold: 2,
                uncertainty_threshold: 3,
                checks_enabled: {
                    ethical_violations: true,
                    critical_failures: true,
                    low_quality: true,
                    high_errors: true,
                    incoherence: true,
                    uncertainty: true,
                    missing_elements: true,
                    short_session: true
                }
            });
        }
    };

    const updateSetting = (key, value) => {
        setSettings({ ...settings, [key]: value });
    };

    const updateCheck = (checkKey, value) => {
        setSettings({
            ...settings,
            checks_enabled: {
                ...settings.checks_enabled,
                [checkKey]: value
            }
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-secondary-900">
                        Manual Review Settings
                    </h1>
                    <p className="text-secondary-500 mt-1">
                        Configure thresholds for automatic manual review flagging
                    </p>
                </div>

                {/* Message */}
                {message && (
                    <div className={`p-4 rounded-lg ${message.type === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                        {message.text}
                    </div>
                )}

                {/* Thresholds Card */}
                <Card>
                    <CardContent className="p-6">
                        <h2 className="text-lg font-bold text-secondary-800 mb-4">Detection Thresholds</h2>

                        <div className="space-y-6">
                            {/* Confidence Threshold */}
                            <div>
                                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                    Confidence Threshold
                                    <span className="text-xs font-normal text-secondary-500 ml-2">
                                        (Sessions below this are auto-flagged)
                                    </span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={settings.confidence_threshold}
                                        onChange={(e) => updateSetting('confidence_threshold', parseFloat(e.target.value))}
                                        className="flex-1"
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={settings.confidence_threshold}
                                        onChange={(e) => updateSetting('confidence_threshold', parseFloat(e.target.value))}
                                        className="w-20 px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                </div>
                                <p className="text-xs text-secondary-500 mt-1">
                                    Current: {(settings.confidence_threshold * 100).toFixed(0)}% - Lower = more flagging
                                </p>
                            </div>

                            {/* Quality Score */}
                            <div>
                                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                    Minimum Quality Score
                                    <span className="text-xs font-normal text-secondary-500 ml-2">
                                        (Average competency score threshold)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="5"
                                    step="0.1"
                                    value={settings.min_quality_score}
                                    onChange={(e) => updateSetting('min_quality_score', parseFloat(e.target.value))}
                                    className="w-32 px-3 py-2 border border-secondary-300 rounded-lg"
                                />
                                <p className="text-xs text-secondary-500 mt-1">
                                    Sessions with average score below this are flagged (scale: 0-5)
                                </p>
                            </div>

                            {/* Error Threshold */}
                            <div>
                                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                    Error Count Threshold
                                    <span className="text-xs font-normal text-secondary-500 ml-2">
                                        (Negative feedback items)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={settings.error_threshold}
                                    onChange={(e) => updateSetting('error_threshold', parseInt(e.target.value))}
                                    className="w-32 px-3 py-2 border border-secondary-300 rounded-lg"
                                />
                                <p className="text-xs text-secondary-500 mt-1">
                                    Sessions with {settings.error_threshold}+ negative feedback items are flagged
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Critical Failures */}
                                <div>
                                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                        Critical Failure Count
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={settings.critical_failure_count}
                                        onChange={(e) => updateSetting('critical_failure_count', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                    <p className="text-xs text-secondary-500 mt-1">
                                        Competencies scored ≤1.0
                                    </p>
                                </div>

                                {/* Session Turns */}
                                <div>
                                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                        Minimum Session Turns
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={settings.min_session_turns}
                                        onChange={(e) => updateSetting('min_session_turns', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                    <p className="text-xs text-secondary-500 mt-1">
                                        Flag if conversation has fewer turns
                                    </p>
                                </div>

                                {/* Incoherence */}
                                <div>
                                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                        Incoherence Threshold
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={settings.incoherence_threshold}
                                        onChange={(e) => updateSetting('incoherence_threshold', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                    <p className="text-xs text-secondary-500 mt-1">
                                        Confusion indicators needed to flag
                                    </p>
                                </div>

                                {/* Uncertainty */}
                                <div>
                                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                                        Uncertainty Threshold
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={settings.uncertainty_threshold}
                                        onChange={(e) => updateSetting('uncertainty_threshold', parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                    <p className="text-xs text-secondary-500 mt-1">
                                        Uncertainty phrases needed to flag
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Enable/Disable Checks */}
                <Card>
                    <CardContent className="p-6">
                        <h2 className="text-lg font-bold text-secondary-800 mb-4">Enable/Disable Checks</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { key: 'ethical_violations', label: 'Ethical/Safety Violations', priority: 'CRITICAL' },
                                { key: 'critical_failures', label: 'Critical Competency Failures', priority: 'CRITICAL' },
                                { key: 'low_quality', label: 'Low Overall Quality', priority: 'HIGH' },
                                { key: 'high_errors', label: 'High Error Count', priority: 'HIGH' },
                                { key: 'incoherence', label: 'Incoherent Dialogue', priority: 'MEDIUM' },
                                { key: 'uncertainty', label: 'Evaluator Uncertainty', priority: 'MEDIUM' },
                                { key: 'missing_elements', label: 'Missing Critical Elements', priority: 'MEDIUM' },
                                { key: 'short_session', label: 'Session Too Short', priority: 'LOW' }
                            ].map(check => (
                                <div key={check.key} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                                    <div>
                                        <div className="font-medium text-secondary-900 text-sm">{check.label}</div>
                                        <div className="text-xs text-secondary-500">Priority: {check.priority}</div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={settings.checks_enabled[check.key]}
                                            onChange={(e) => updateCheck(check.key, e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-secondary-300 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Meta Info */}
                {settings.updated_at && (
                    <Card>
                        <CardContent className="p-4 bg-secondary-50">
                            <div className="text-sm text-secondary-600">
                                Last updated: {new Date(settings.updated_at).toLocaleString()}
                                {settings.updated_by && ` by ${settings.updated_by}`}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Actions */}
                <div className="flex gap-4">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                    <button
                        onClick={handleReset}
                        className="px-6 py-3 bg-secondary-200 text-secondary-700 rounded-lg hover:bg-secondary-300 transition-colors font-medium"
                    >
                        Reset to Defaults
                    </button>
                </div>
            </div>
        </DashboardLayout>
    );
}
